/**
 * Buff aura skill for Officer enemies — boosts damage and speed of allies within radius.
 * Windup → active (buff zone for duration) → recovery → cooldown.
 * While active, exposes radius and multipliers for the EnemyBuffManager to query.
 */
import { PhasedSkillEffect } from '../PhasedSkillEffect';
import type { SkillPhase } from '../PhasedSkillEffect';
import type { EnemySkillEffect, EnemySkillMovement, EnemySkillDamageInfo } from './EnemySkillEffect';

export interface EnemyBuffConfig {
  buffRadius: number;
  buffDurationMs: number;
  buffDamageMultiplier: number;
  buffSpeedMultiplier: number;
  windupMs: number;
  recoveryMs: number;
  cooldownMs: number;
}

/** Applies damage and speed multipliers to nearby allies for a timed duration. */
export class EnemyBuffSkillEffect extends PhasedSkillEffect implements EnemySkillEffect {
  readonly skillId: string;
  protected readonly phaseSequence = ['windup', 'active', 'recovery', 'cooldown'] as const;
  private readonly config: EnemyBuffConfig;

  /** Officer position — updated each frame for radius check. */
  private _originX: number = 0;
  private _originY: number = 0;

  constructor(skillId: string, config: EnemyBuffConfig) {
    super();
    this.skillId = skillId;
    this.config = config;
  }

  protected getPhaseDuration(phase: SkillPhase): number {
    switch (phase) {
      case 'windup': return this.config.windupMs;
      case 'active': return this.config.buffDurationMs;
      case 'recovery': return this.config.recoveryMs;
      case 'cooldown': return this.config.cooldownMs;
      default: return 0;
    }
  }

  /** Set the Officer's current position for buff radius checks. */
  setOrigin(x: number, y: number): void {
    this._originX = x;
    this._originY = y;
  }

  isInDamagePhase(): boolean { return false; }

  getMovement(): EnemySkillMovement | null {
    if (this.phase === 'windup' || this.phase === 'active' || this.phase === 'recovery') {
      return { vx: 0, vy: 0 };
    }
    return null;
  }

  getDamageInfo(): EnemySkillDamageInfo | null { return null; }

  // --- Buff-specific API ---

  /** True when the buff aura is actively boosting nearby allies. */
  isBuffActive(): boolean { return this.phase === 'active'; }

  get buffRadius(): number { return this.config.buffRadius; }
  get buffDamageMultiplier(): number { return this.config.buffDamageMultiplier; }
  get buffSpeedMultiplier(): number { return this.config.buffSpeedMultiplier; }
  get originX(): number { return this._originX; }
  get originY(): number { return this._originY; }
}
